/** Generate an SVG path string for a sparkline from a list of close prices.
 *  Returns null if the input has fewer than 2 points (no line to draw). */
export function buildSparklinePath(closes: number[], width: number, height: number, padding = 1): string | null {
  if (closes.length < 2) return null;
  const min = Math.min(...closes);
  const max = Math.max(...closes);
  const span = max - min || 1;
  const w = width - padding * 2;
  const h = height - padding * 2;
  const stepX = w / (closes.length - 1);
  const points = closes.map((c, i) => {
    const x = padding + i * stepX;
    const y = padding + h - ((c - min) / span) * h;
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
  });
  return points.join(' ');
}

/** First → last percent change for color-coding the sparkline stroke. */
export function sparklineChange(closes: number[]): number {
  if (closes.length < 2) return 0;
  const first = closes[0];
  const last = closes[closes.length - 1];
  if (!first) return 0;
  return ((last - first) / first) * 100;
}

/** Geometry for a "trend" sparkline — a line plus a closed area polygon for
 *  the gradient fill plus the coordinates of the last point (for an end-dot).
 *  Computed in pixel space (not a 0-100 viewBox) so the end-dot renders as a
 *  true circle and the gradient isn't stretched — the price-trend cell uses a
 *  fixed pixel width. */
export interface TrendSparklineGeometry {
  /** "x,y x,y …" for the <polyline> stroke. */
  linePoints: string;
  /** Polygon points closing down to the baseline for the gradient <polygon>.
   *  Empty string when the series is flat (no fill drawn). */
  areaPoints: string;
  /** Coordinates of the most-recent point — where the end-dot is drawn. */
  lastX: number;
  lastY: number;
  /** Net direction across the window (last >= first). Callers may override the
   *  display colour (e.g. follow the day's % change) but this is the default. */
  isUp: boolean;
  /** True when every value is equal — render a centered baseline, no fill. */
  flat: boolean;
}

/** Build the pixel-space geometry for a TrendSparkline. Returns null when
 *  there aren't at least two finite points to draw a line from. */
export function buildTrendSparkline(
  closes: number[] | null | undefined,
  width: number,
  height: number,
  padY = 3,
): TrendSparklineGeometry | null {
  if (!closes) return null;
  // Drop non-finite points (a null / NaN / Infinity can slip in from sparse
  // intraday data). Plotting only the clean series means one bad bucket can't
  // poison the path with a NaN coordinate or flip the direction colour.
  const pts = closes.filter((c) => Number.isFinite(c));
  if (pts.length < 2) return null;
  let min = Infinity;
  let max = -Infinity;
  for (const c of pts) {
    if (c < min) min = c;
    if (c > max) max = c;
  }
  const n = pts.length;
  const isUp = pts[n - 1] >= pts[0];
  if (min === max) {
    const y = height / 2;
    return {
      linePoints: `0,${y.toFixed(2)} ${width.toFixed(2)},${y.toFixed(2)}`,
      areaPoints: '',
      lastX: width,
      lastY: y,
      isUp,
      flat: true,
    };
  }
  const span = max - min;
  const innerH = height - padY * 2;
  const stepX = width / (n - 1);
  const xs: number[] = new Array(n);
  const ys: number[] = new Array(n);
  for (let i = 0; i < n; i++) {
    xs[i] = i * stepX;
    ys[i] = padY + innerH - ((pts[i] - min) / span) * innerH;
  }
  const linePoints = xs.map((x, i) => `${x.toFixed(2)},${ys[i].toFixed(2)}`).join(' ');
  // Close the path down to the baseline at both ends so the gradient fills the
  // area under the line.
  const areaPoints = `0,${height.toFixed(2)} ${linePoints} ${width.toFixed(2)},${height.toFixed(2)}`;
  return { linePoints, areaPoints, lastX: xs[n - 1], lastY: ys[n - 1], isUp, flat: false };
}
